import { ReactNode, RefObject, useRef } from 'react';

import { reportMykissClientError } from '@lib/errors/client';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { ElementDimensions, useElementDimensions } from '@hooks/useElementDimensions';
import { OverflowDirection, useElementIsOverflowing } from '@hooks/useElementIsOverflowing';
import { useIsTouchDevice } from '@hooks/useIsTouchDevice';
import { useScrollState } from '@hooks/useScrollState';
import styles from '../Carousel.module.css';
import { ImagePageIndicator } from '../components/ImagePageIndicator';
import { PageIndicator } from '../components/PageIndicator';
import { ScrollButton } from '../components/ScrollButton';
import { onNextClicked, onPreviousClicked } from '../helpers';

interface IProps {
  children: ReactNode[] | ReactNode;
  withImageList?: boolean;
  id: string;
  /* If true, the carousel will expand to the edge of the page when used as a descendant of a Container */
  fullWidth?: boolean;
  withoutDefaultContainerClasses?: boolean;
  integratedIndicators?: boolean;
  lightBackground?: boolean;
  totalPagesOverride?: { active: boolean; totalPages: number };
  className?: string;
  innerWrapperClassName?: string;
  innerClassName?: string;
  dataTestId?: string;
  pageIndicatorClassName?: string;
  pageIndicatorSize?: 'medium' | 'large';
}

// This helps to correctly pick the next/previous carousel item
const CONTAINER_OFFSET = 20;

function getCurrentPage(
  totalPages: number,
  scrollPosition: number,
  dimensions: ElementDimensions | null,
): number {
  if (dimensions) {
    const pageWidth = dimensions.scrollWidth / totalPages;
    return Math.min(Math.round(scrollPosition / pageWidth) + 1, totalPages);
  }
  return 1;
}

const scrollToPage = (page: number, containerRef: RefObject<HTMLDivElement | null>): void => {
  const { current } = containerRef;
  if (current) {
    [...current.children][page - 1]!.scrollIntoView({
      block: 'nearest',
      inline: 'end',
      behavior: 'smooth',
    });
  }
};

export const DiscreteCarousel = ({
  withImageList,
  children,
  id,
  fullWidth = false,
  withoutDefaultContainerClasses = false,
  integratedIndicators = false,
  lightBackground = true,
  totalPagesOverride = { active: false, totalPages: 0 },
  className = '',
  innerClassName = '',
  dataTestId = '',
  innerWrapperClassName = '',
  pageIndicatorClassName,
  pageIndicatorSize = 'medium',
}: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const isTouchDevice = useIsTouchDevice();
  const containerRef = useRef<HTMLDivElement>(null);
  const { atEnd, atStart, scrollPosition } = useScrollState(containerRef);
  const isOverflowing = useElementIsOverflowing(containerRef, OverflowDirection.EITHER, [children]);
  const dimensions = useElementDimensions(containerRef, [children]);

  const pagesCount = Array.isArray(children) ? children.length : 1;
  const totalPages = totalPagesOverride.active ? totalPagesOverride.totalPages : pagesCount;
  if (!totalPages) {
    const values = { children, pagesCount, totalPages };
    reportMykissClientError(new Error(JSON.stringify(values)), undefined, 'info');
  }
  const currentPage = getCurrentPage(totalPages, scrollPosition, dimensions);

  return (
    <div
      className={`${className} flex flex-col ${
        fullWidth ? '-mx-6 sm:-mx-11 md:-mx-14 lg:-mx-16' : ''
      } ${styles.carousel}`}
      data-testid={dataTestId}
    >
      <div className={`flex ${innerWrapperClassName} ${integratedIndicators ? 'relative' : ''}`}>
        {!isTouchDevice && isOverflowing && !atStart && (
          <ScrollButton
            direction="left"
            onClick={() => {
              onPreviousClicked(containerRef, CONTAINER_OFFSET);
            }}
          />
        )}
        <div
          ref={containerRef}
          className={`${styles.container} ${
            withoutDefaultContainerClasses
              ? innerClassName
              : `${innerClassName} space-x-3 sm:space-x-4`
          } ${fullWidth ? 'pl-6 sm:pl-11 md:pl-14 lg:pl-16' : ''} ${
            integratedIndicators ? 'relative' : ''
          }`}
        >
          {children}
          {/* The below line is a bit of a hack to get around the fact that overflow-scroll won't recognise padding-right */}
          {fullWidth && clientHasRendered && <div className="pl-6 sm:pl-11 md:pl-14 lg:pl-16" />}
        </div>
        {!isTouchDevice && isOverflowing && !atEnd && (
          <ScrollButton
            direction="right"
            onClick={() => {
              onNextClicked(containerRef, CONTAINER_OFFSET);
            }}
          />
        )}
        {integratedIndicators && (
          <div className="z-high absolute bottom-8 left-[50%] -translate-x-1/2">
            <PageIndicator
              currentPage={currentPage}
              totalPages={totalPages}
              onClick={page => {
                scrollToPage(page, containerRef);
              }}
              id={id}
              smallIndicatorSpacing
              lightBackground={lightBackground}
              pageIndicatorSize={pageIndicatorSize}
            />
          </div>
        )}
      </div>

      {!integratedIndicators && !withImageList && (
        <PageIndicator
          className={pageIndicatorClassName}
          currentPage={currentPage}
          totalPages={totalPages}
          onClick={page => {
            scrollToPage(page, containerRef);
          }}
          id={id}
          lightBackground={lightBackground}
          pageIndicatorSize={pageIndicatorSize}
        />
      )}
      {!integratedIndicators && withImageList && (
        <ImagePageIndicator
          currentPage={currentPage}
          totalPages={children}
          onClick={page => {
            scrollToPage(page, containerRef);
          }}
          id={id}
          lightBackground={lightBackground}
        />
      )}
    </div>
  );
};
